import { Injectable } from '@angular/core';

interface EncryptedPayload {
  ciphertext: string;  // base64
  iv: string;          // base64
}

interface StoredTokenEntry {
  company: string;
  payload: EncryptedPayload;
  expiresAt: string;   // ISO date – stored alongside so we can restore CompanyTokenData.expiresAt
  userId: number;
}

const DB_NAME = 'tibss_auth_db';
const DB_VERSION = 1;
const TOKEN_STORE = 'encrypted_tokens';
const KEY_STORE = 'crypto_keys';
const CRYPTO_KEY_ID = 'master';

@Injectable({
  providedIn: 'root'
})
export class CryptoTokenStorageService {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private cryptoKeyPromise: Promise<CryptoKey> | null = null;

  // ───── Public API ─────────────────────────────────────────────

  async storeToken(company: string, token: string, expiresAt: Date, userId: number): Promise<void> {
    const key = await this.getCryptoKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encoded = new TextEncoder().encode(token);
    const cipherBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

    const entry: StoredTokenEntry = {
      company,
      payload: {
        ciphertext: this.bufferToBase64(cipherBuffer),
        iv: this.bufferToBase64(iv.buffer)
      },
      expiresAt: expiresAt.toISOString(),
      userId
    };

    const db = await this.openDB();
    await this.idbPut(db, TOKEN_STORE, entry);
  }

  async getToken(company: string): Promise<{ token: string; expiresAt: Date; userId: number } | null> {
    try {
      const db = await this.openDB();
      const entry = await this.idbGet<StoredTokenEntry>(db, TOKEN_STORE, company);
      if (!entry) return null;

      const key = await this.getCryptoKey();
      const cipherBuffer = this.base64ToBuffer(entry.payload.ciphertext);
      const iv = this.base64ToBuffer(entry.payload.iv);

      const plainBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv) }, key, cipherBuffer);
      const token = new TextDecoder().decode(plainBuffer);

      return {
        token,
        expiresAt: new Date(entry.expiresAt),
        userId: entry.userId
      };
    } catch {
      // Decryption failure (e.g. key was wiped) – treat as no token
      return null;
    }
  }

  async removeToken(company: string): Promise<void> {
    const db = await this.openDB();
    await this.idbDelete(db, TOKEN_STORE, company);
  }

  async clearAll(): Promise<void> {
    const db = await this.openDB();
    await this.idbClear(db, TOKEN_STORE);
    await this.idbClear(db, KEY_STORE);
    this.cryptoKeyPromise = null;
  }

  // ───── Crypto key management ──────────────────────────────────

  private getCryptoKey(): Promise<CryptoKey> {
    if (!this.cryptoKeyPromise) {
      this.cryptoKeyPromise = this.loadOrCreateKey();
    }
    return this.cryptoKeyPromise;
  }

  private async loadOrCreateKey(): Promise<CryptoKey> {
    const db = await this.openDB();
    const existing = await this.idbGet<CryptoKey>(db, KEY_STORE, CRYPTO_KEY_ID);
    if (existing) return existing;

    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false, // non-extractable – cannot be read via JS
      ['encrypt', 'decrypt']
    );

    await this.idbPut(db, KEY_STORE, key, CRYPTO_KEY_ID);
    return key;
  }

  // ───── IndexedDB helpers ──────────────────────────────────────

  private openDB(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(TOKEN_STORE)) {
            db.createObjectStore(TOKEN_STORE, { keyPath: 'company' });
          }
          if (!db.objectStoreNames.contains(KEY_STORE)) {
            db.createObjectStore(KEY_STORE);
          }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
    return this.dbPromise;
  }

  private idbPut(db: IDBDatabase, store: string, value: any, key?: IDBValidKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store, 'readwrite');
      const req = key !== undefined ? tx.objectStore(store).put(value, key) : tx.objectStore(store).put(value);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private idbGet<T>(db: IDBDatabase, store: string, key: IDBValidKey): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store, 'readonly');
      const req = tx.objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    });
  }

  private idbDelete(db: IDBDatabase, store: string, key: IDBValidKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store, 'readwrite');
      const req = tx.objectStore(store).delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private idbClear(db: IDBDatabase, store: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store, 'readwrite');
      const req = tx.objectStore(store).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // ───── Encoding helpers ───────────────────────────────────────

  private bufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
